/**
 * The UI locale (redesign, D-145). Kept apart from i18next so the date and
 * number helpers can read it without importing the translation runtime.
 *
 * French is the product's source language and the default since every screen
 * is translated (redesign Phase 12). `?lang=en` opens the English UI and is
 * remembered in this browser, as is `?lang=fr`.
 */

export type AppLocale = 'fr' | 'en';

export const LOCALES: readonly AppLocale[] = ['fr', 'en'];

export const DEFAULT_LOCALE: AppLocale = 'fr';

const STORAGE_KEY = 'repliq.lang';

let active: AppLocale = DEFAULT_LOCALE;

export function isLocale(value: unknown): value is AppLocale {
  return value === 'fr' || value === 'en';
}

/** The locale the UI renders in right now. */
export function currentLocale(): AppLocale {
  return active;
}

/** Called by the i18n layer whenever the language changes. */
export function setActiveLocale(locale: AppLocale): void {
  active = locale;
}

function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    // Blocked storage: the choice simply isn't remembered.
    return null;
  }
}

/**
 * `?lang=` wins and is remembered; then the remembered choice; then the default.
 * Storage that throws (private mode, blocked cookies) never breaks the page.
 */
export function resolveInitialLocale(search: string = window.location.search): AppLocale {
  const fromUrl = new URLSearchParams(search).get('lang');
  if (isLocale(fromUrl)) {
    try {
      storage()?.setItem(STORAGE_KEY, fromUrl);
    } catch {
      // Not remembered; the URL still applies to this page.
    }
    return fromUrl;
  }
  try {
    const saved = storage()?.getItem(STORAGE_KEY);
    if (isLocale(saved)) return saved;
  } catch {
    // Unreadable storage: fall through to the default.
  }
  return DEFAULT_LOCALE;
}
